import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import i18n from "i18next";
import { FlexboxGrid, Form, Message } from "rsuite";
import Checkbox from "rsuite/esm/Checkbox";
import ReCAPTCHA from "react-google-recaptcha";

import classes from "./FamilyTrialRegister.module.scss";
import PromotionSection from "./PromotionSection";
import SecondaryInput from "components/atoms/SecondaryInput";
import SecondaryFormLabel from "components/atoms/SecondaryFormLabel";
import SecondaryButton from "components/atoms/SecondaryButton";
import { isValidEmail } from "service/utils/ValidationUtil";
import ConsoleAPIClient from "service/utils/ConsoleAPIClient";
import { useErrorMessages } from "hooks/useErrorMessage";
import {
  FAMILY_PROGRAM_TERMS_URL,
  initialFormValues,
} from "config/MrFamilyTrialFormConfig";
import { FamilyTenantCreateRequest, FamilyTenantFormValues } from "./type";

const FamilyTrialRegister: React.FC = () => {
  // hooks
  const navigate = useNavigate();
  const { errors, showErrorMessage, clearErrors } = useErrorMessages();
  const errorRef = useRef<HTMLDivElement>(null);

  // states
  const [values, setValues] = useState<FamilyTenantFormValues>(initialFormValues);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [progressMessage, setProgressMessage] = useState<string>("");

  // useEffect
  useEffect(() => {
    document.title = i18n.t("register_form_mr_family.page_title") as string;
  }, []);

  useEffect(() => {
    if (errors.length > 0 && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [errors]);

  // handlers
  const handleValueInputChange = (value: any, event: any) => {
    const name: string | null = event?.currentTarget?.getAttribute("name");
    if (name == null || !(name in values)) {
      return;
    }
    setValues((prev) => ({ ...prev, [name]: value.toString() }));
  };

  const handleCheckboxChange =
    (name: "accepted_privacy" | "accepted_terms") =>
    (_value: any, checked: boolean) => {
      setValues((prev) => ({ ...prev, [name]: checked ? "1" : "0" }));
    };

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    clearErrors();
    const errorMessages: string[] = [];

    if (
      !values.user_last_name ||
      !values.user_first_name ||
      !values.user_email ||
      !values.employee_email
    ) {
      errorMessages.push(
        i18n.t("register_form_mr_family.error.messages.required") as string
      );
    }
    if (values.user_email && !isValidEmail(values.user_email)) {
      errorMessages.push(
        i18n.t("register_form_mr_family.error.messages.user_email") as string
      );
    }
    if (values.employee_email && !isValidEmail(values.employee_email)) {
      errorMessages.push(
        i18n.t(
          "register_form_mr_family.error.messages.employee_email"
        ) as string
      );
    }
    if (values.accepted_privacy === "0") {
      errorMessages.push(
        i18n.t(
          "register_form_mr_family.error.messages.accepted_privacy"
        ) as string
      );
    }
    if (values.accepted_terms === "0") {
      errorMessages.push(
        i18n.t(
          "register_form_mr_family.error.messages.accepted_terms"
        ) as string
      );
    }
    if (!recaptchaToken) {
      errorMessages.push(
        i18n.t("register_form_mr_family.error.messages.recaptcha") as string
      );
    }

    if (errorMessages.length > 0) {
      showErrorMessage(errorMessages);
      setIsSubmitting(false);
      return;
    }

    try {
      setProgressMessage(
        i18n.t("register_form_mr_family.progress_messages.registering") as string
      );
      const requestBody: FamilyTenantCreateRequest = {
        family_tenant: {
          user_last_name: values.user_last_name,
          user_first_name: values.user_first_name,
          user_email: values.user_email,
          employee_email: values.employee_email,
        },
      };
      await ConsoleAPIClient.postWithoutAuth(
        "/api/jp/v1/family-tenants",
        requestBody
      );
      setProgressMessage("");
      navigate("/finished?pageType=application");
    } catch (error: any) {
      console.error("Error in FamilyTrialRegister when submit", error);
      // 社員マスタ照合エラーの400のみ文言を表示（他の400は生エラー漏洩を避け汎用へ）
      const status = error?.response?.status;
      const apiMessage: string | undefined =
        error?.response?.data?.errors?.[0]?.message;
      const isEmployeeMasterError =
        !!apiMessage &&
        (apiMessage.includes("社員マスタ") || apiMessage.includes("従業員番号"));
      if (status === 400 && isEmployeeMasterError) {
        showErrorMessage([apiMessage as string]);
      } else {
        showErrorMessage();
      }
      setProgressMessage("");
      setIsSubmitting(false);
    }
  }, [values, recaptchaToken, clearErrors, showErrorMessage, navigate]);

  return (
    <div className={classes["body"]}>
      <FlexboxGrid className={classes["form-container"]}>
        <Form className={classes["form-frame"]}>
          <h3 className={classes["form-frame-title"]}>
            {i18n.t("register_form_mr_family.form_title") as string}
          </h3>
          {errors.length > 0 ? (
            <Form.Group className={classes["form-input-group"]}>
              <Message
                className={classes["message-alert"]}
                showIcon
                type="error"
                header="Error"
                ref={errorRef}
              >
                {errors.map((error, idx) => {
                  return <p key={idx}>※ {error}</p>;
                })}
              </Message>
            </Form.Group>
          ) : null}

          <Form.Group controlId="user_name" className={classes["form-input-group"]}>
            <SecondaryFormLabel
              text={i18n.t("register_form_mr_family.user_name") as string}
              required={true}
            />
            <FlexboxGrid justify="space-between" style={{ width: "100%" }}>
              <FlexboxGrid.Item
                colspan={11}
                className={classes["input-name-flexgrid-left"]}
              >
                <SecondaryInput
                  name="user_last_name"
                  value={values.user_last_name}
                  onChange={handleValueInputChange}
                  type="text"
                  disabled={false}
                  step=""
                  placeHolder={
                    i18n.t("register_form_mr_family.samples.last_name") as string
                  }
                />
              </FlexboxGrid.Item>
              <FlexboxGrid.Item
                colspan={11}
                className={classes["input-name-flexgrid-right"]}
              >
                <SecondaryInput
                  name="user_first_name"
                  value={values.user_first_name}
                  onChange={handleValueInputChange}
                  type="text"
                  disabled={false}
                  step=""
                  placeHolder={
                    i18n.t("register_form_mr_family.samples.first_name") as string
                  }
                />
              </FlexboxGrid.Item>
            </FlexboxGrid>
          </Form.Group>

          <Form.Group controlId="user_email" className={classes["form-input-group"]}>
            <SecondaryFormLabel
              text={i18n.t("register_form_mr_family.user_email") as string}
              required={true}
            />
            <SecondaryInput
              name="user_email"
              value={values.user_email}
              onChange={handleValueInputChange}
              type="email"
              disabled={false}
              step=""
              placeHolder={
                i18n.t("register_form_mr_family.samples.user_email") as string
              }
            />
          </Form.Group>

          <Form.Group
            controlId="employee_email"
            className={classes["form-input-group"]}
          >
            <SecondaryFormLabel
              text={i18n.t("register_form_mr_family.employee_email") as string}
              required={true}
            />
            <SecondaryInput
              name="employee_email"
              value={values.employee_email}
              onChange={handleValueInputChange}
              type="email"
              disabled={false}
              step=""
              placeHolder={
                i18n.t(
                  "register_form_mr_family.samples.employee_email"
                ) as string
              }
            />
          </Form.Group>

          <Form.Group
            controlId="accepted_privacy"
            className={classes["form-input-group"]}
          >
            <FlexboxGrid className={classes["accept-checkbox"]}>
              <Checkbox
                name="accepted_privacy"
                checked={values.accepted_privacy === "1"}
                onChange={handleCheckboxChange("accepted_privacy")}
                disabled={false}
              >
                <span className={classes["accept-checkbox-text"]}>
                  <a
                    href="https://www.revcomm.com/ja/privacypolicy"
                    target="_blank"
                    rel="noreferrer noopener"
                    className={classes["accept-checkbox-text-link"]}
                  >
                    個人情報の取り扱い
                  </a>{" "}
                  と{" "}
                  <a
                    href="https://www.revcomm.com/ja/personal_information_protection_policy/"
                    target="_blank"
                    rel="noreferrer noopener"
                    className={classes["accept-checkbox-text-link"]}
                  >
                    個人情報保護方針
                  </a>{" "}
                  に同意する
                </span>
              </Checkbox>
            </FlexboxGrid>
          </Form.Group>

          <Form.Group
            controlId="accepted_terms"
            className={classes["form-input-group"]}
          >
            <FlexboxGrid className={classes["accept-checkbox"]}>
              <Checkbox
                name="accepted_terms"
                checked={values.accepted_terms === "1"}
                onChange={handleCheckboxChange("accepted_terms")}
                disabled={false}
              >
                <span className={classes["accept-checkbox-text"]}>
                  <a
                    href={FAMILY_PROGRAM_TERMS_URL}
                    target="_blank"
                    rel="noreferrer noopener"
                    className={classes["accept-checkbox-text-link"]}
                  >
                    利用規約
                  </a>{" "}
                  に同意する
                </span>
              </Checkbox>
            </FlexboxGrid>
          </Form.Group>

          <Form.Group className={classes["form-input-group"]}>
            <ReCAPTCHA
              sitekey="6LdfqOQZAAAAAFe8i_GhcQWu39jG2fcp85xOcKUu"
              onChange={(token) => setRecaptchaToken(token)}
              style={{ margin: "0px", justifyContent: "center" }}
            />
          </Form.Group>

          <Form.Group controlId="submit" className={classes["form-input-group"]}>
            <SecondaryButton
              text={i18n.t("register_form_mr_family.submit") as string}
              onClick={handleSubmit}
              isLoading={isSubmitting}
            />
          </Form.Group>

          {isSubmitting && progressMessage !== "" ? (
            <Form.Group className={classes["form-input-group"]}>
              <Message
                className={classes["message-alert"]}
                showIcon
                type="info"
                header="しばらくお待ち下さい。"
              >
                <p>{progressMessage}</p>
              </Message>
            </Form.Group>
          ) : null}
        </Form>
      </FlexboxGrid>
      <div className={classes["line"]}>
        <div className={classes["line2"]}></div>
      </div>
      <div className={classes["section-right"]}>
        <PromotionSection />
      </div>
    </div>
  );
};

export default FamilyTrialRegister;
